import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CaretLeft, CaretRight, Printer, ArrowSquareOut, MagicWand } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { formatEUR, formatDate } from "@/lib/constants";
import { printPdfBlob } from "@/lib/printPdf";
import AreasStep from "@/components/rentincrease/AreasStep";
import { StatusPill } from "@/components/rentincrease/riMeta";

const LEVEL = { unit: "Einheit", property: "Objekt", district: "Stadtteil", city: "Stadt" };
const STEPS = ["Vergleichsmieten je Stadt/Stadtteil", "Mietverhältnisse durchgehen", "Verlangen erzeugen"];

function TenancyCard({ r, selected, onToggle, onReload }) {
  const [district, setDistrict] = useState("");
  const cr = r.comparative_rent || {};
  const saveDistrict = async () => {
    await api.put(`/rent-increase/properties/${r.property_id}/district`, { district });
    toast.success("Stadtteil am Objekt gespeichert"); onReload();
  };
  return (
    <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid="wizard-tenancy-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-sm">{r.contract_number}</span>
        <span className="text-sm text-slate-600">{r.property_name}{r.unit_number ? ` · ${r.unit_number}` : ""}{r.unit_location ? ` · ${r.unit_location}` : ""}</span>
        <StatusPill status={r.status} label={r.status_label} testId="wizard-status" />
        <Link to={`/mietverhaeltnisse/${r.tenancy_id}?tab=mieterhoehung`} target="_blank" className="ml-auto text-xs text-teal-700 inline-flex items-center gap-1"><ArrowSquareOut size={12} /> Mietvertrag</Link>
      </div>
      <p className="text-sm text-slate-700">{r.reason}</p>
      <div className="grid sm:grid-cols-4 gap-3 text-sm">
        <div><div className="text-[11px] text-slate-500">Aktuell</div><div className="tabular-nums">{formatEUR(r.current_rent)}{r.current_per_m2 ? ` · ${r.current_per_m2.toFixed(2)} €/m²` : ""}</div></div>
        <div><div className="text-[11px] text-slate-500">Vergleichsmiete ({LEVEL[cr.level] || "fehlt"})</div><div className="tabular-nums">{cr.mid ? `${cr.low ?? "–"} / ${cr.mid} / ${cr.high ?? "–"} €/m²` : "—"}</div>{cr.area_label && <div className="text-[10px] text-slate-500">{cr.area_label}</div>}</div>
        <div><div className="text-[11px] text-slate-500">Neu (max.)</div><div className="tabular-nums font-medium text-emerald-700" data-testid="wizard-new-rent">{r.increase_eur > 0 ? `${formatEUR(r.new_rent)} (+${formatEUR(r.increase_eur)})` : "—"}</div></div>
        <div><div className="text-[11px] text-slate-500">Wirksam ab</div><div>{r.increase_eur > 0 ? formatDate(r.earliest_effective_date) : "—"}</div></div>
      </div>
      {r.status === "no_data" && (
        <div className="flex flex-wrap items-center gap-2 text-xs bg-slate-50 rounded p-2">
          Stadtteil für {r.property_name} festlegen: <Input className="h-7 w-44 text-xs" value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="z. B. Prenzlauer Berg" data-testid="wizard-district-input" />
          <Button size="sm" variant="outline" className="h-7" onClick={saveDistrict} data-testid="wizard-district-save">Speichern</Button>
          <span className="text-slate-500">oder in Schritt 1 einen Stadtwert pflegen.</span>
        </div>
      )}
      {["possible_now", "possible_from"].includes(r.status) && (
        <label className="flex items-center gap-2 text-sm cursor-pointer"><Checkbox checked={selected} onCheckedChange={onToggle} data-testid="wizard-include" /> In den Sammeldruck aufnehmen</label>
      )}
    </div>
  );
}

export default function MieterhoehungAssistent() {
  const [step, setStep] = useState(0);
  const [rows, setRows] = useState([]);
  const [idx, setIdx] = useState(0);
  const [sel, setSel] = useState({});
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/rent-increase/portfolio").then(({ data }) => {
    const list = data.rows.filter((r) => r.status !== "excluded" && r.status !== "error");
    setRows(list);
    setSel((s) => (Object.keys(s).length ? s : Object.fromEntries(list.filter((r) => r.status === "possible_now").map((r) => [r.tenancy_id, true]))));
  }), []);
  useEffect(() => { if (step > 0) load(); }, [step, load]);
  const chosen = useMemo(() => rows.filter((r) => sel[r.tenancy_id] && ["possible_now", "possible_from"].includes(r.status)), [rows, sel]);
  const r = rows[Math.min(idx, rows.length - 1)];

  const batch = async () => {
    setBusy(true);
    try {
      const res = await api.post("/rent-increase/batch-print", { tenancy_ids: chosen.map((x) => x.tenancy_id) }, { responseType: "blob" });
      printPdfBlob(res.data);
      const sk = Number(res.headers?.["x-skipped"] || 0);
      toast.success(`${chosen.length - sk} Verlangen inkl. Rückantwort erzeugt und abgelegt${sk ? ` · ${sk} übersprungen` : ""}`);
    } catch { toast.error("Sammeldruck fehlgeschlagen"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5" data-testid="mieterhoehung-assistent">
      <div className="flex items-center gap-2">
        <MagicWand size={24} />
        <h1 className="text-2xl font-semibold text-slate-900">Mieterhöhungs-Assistent</h1>
        <Link to="/mieterhoehungen" className="ml-auto text-sm text-teal-700">Zur Übersicht</Link>
      </div>
      <div className="flex gap-2" data-testid="wizard-steps">
        {STEPS.map((s, i) => (
          <button key={s} type="button" onClick={() => setStep(i)} data-testid={`wizard-step-${i}`}
            className={`flex-1 text-left rounded-md border px-3 py-2 text-xs ${step === i ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600"}`}>
            <div className="font-semibold">Schritt {i + 1}</div>{s}
          </button>
        ))}
      </div>
      {step === 0 && <AreasStep />}
      {step === 1 && (rows.length === 0 ? <div className="text-sm text-slate-500">Keine prüfbaren Mietverhältnisse.</div> : r && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm">
            <Button size="icon" variant="outline" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} data-testid="wizard-prev"><CaretLeft size={16} /></Button>
            <span data-testid="wizard-position">{idx + 1} von {rows.length}</span>
            <Button size="icon" variant="outline" onClick={() => setIdx(Math.min(rows.length - 1, idx + 1))} disabled={idx >= rows.length - 1} data-testid="wizard-next"><CaretRight size={16} /></Button>
            <span className="ml-auto text-xs text-slate-500">{chosen.length} für den Sammeldruck ausgewählt</span>
          </div>
          <TenancyCard key={r.tenancy_id} r={r} selected={!!sel[r.tenancy_id]} onToggle={(v) => setSel((s) => ({ ...s, [r.tenancy_id]: !!v }))} onReload={load} />
        </div>
      ))}
      {step === 2 && (
        <div className="space-y-3" data-testid="wizard-batch">
          <p className="text-sm text-slate-600">Jedes Verlangen wird vollständig ausgefüllt mit Zustimmungserklärung und QR-Upload als eigenes Dokument am Mietvertrag abgelegt und gesammelt gedruckt.</p>
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
            {chosen.map((x) => (
              <div key={x.tenancy_id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="font-mono text-xs w-28">{x.contract_number}</span><span className="flex-1 truncate">{x.property_name}{x.unit_number ? ` · ${x.unit_number}` : ""}</span>
                <span className="tabular-nums">{formatEUR(x.current_rent)} → {formatEUR(x.new_rent)}</span>
                <span className="text-xs text-slate-500">ab {formatDate(x.earliest_effective_date)}</span>
              </div>
            ))}
            {chosen.length === 0 && <div className="p-4 text-sm text-slate-500">Keine Mietverhältnisse ausgewählt (Schritt 2).</div>}
          </div>
          <Button className="gap-1" onClick={batch} disabled={busy || !chosen.length} data-testid="wizard-batch-print"><Printer size={16} /> {busy ? "Erzeuge…" : `${chosen.length} Verlangen drucken`}</Button>
        </div>
      )}
      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} data-testid="wizard-back">Zurück</Button>
        {step < 2 && <Button onClick={() => setStep(step + 1)} data-testid="wizard-continue">Weiter</Button>}
      </div>
    </div>
  );
}
